import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import { MaterialService } from '../material/material.service';
import { PanelStylesService } from '../panel-styles/panel-styles.service';
import {HeightService} from '../height/height.service';
import {WorksurfaceService} from '../worksurface/worksurface.service';
import {DoorService} from '../door/door.service';
import {TitleCasePipe} from '@angular/common';

@Component({
  selector: 'app-visuals',
  imports: [
    TitleCasePipe
  ],
  templateUrl: './visuals.html',
  styleUrl: './visuals.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Visuals {
  private panelStylesService = inject(PanelStylesService);
  protected materialService = inject(MaterialService);
  private heightService = inject(HeightService);
  private worksurfaceService = inject(WorksurfaceService);
  private doorService = inject(DoorService);

  protected selectedHeight = computed(() =>this.heightService.selectedHeight());
  protected selectedWorksurface = computed(() => this.worksurfaceService.selectedWorksurface());

  protected selectedPanelStyle = computed(() => this.panelStylesService.selectedStyle());

  protected t0Mat = this.materialService.getSelection('T0');
  protected t1Mat = this.materialService.getSelection('T1');
  protected t2Mat = this.materialService.getSelection('T2');
  protected t3Mat = this.materialService.getSelection('T3');

  protected worksurface = this.worksurfaceService.selectedWorksurface;
  protected door = this.doorService.selectedDoor;

  readonly zones = computed(() => {
    const height = this.heightService.selectedHeight();
    const style = this.panelStylesService.selectedStyle();

    let result = ['T3', 'T2', 'T1', 'T0'];

    if (height == 32) {
      result = result.filter(tab => tab !== 'T3');
      result = result.filter(tab => tab !== 'T2');
      if (style?.endsWith('B')) result = result.filter(tab => tab !== 'T1');
    } else if (height === 39) {
      result = result.filter(tab => tab !== 'T3');
      if (style?.endsWith('B') || style?.endsWith('D')) result = result.filter(tab => tab !== 'T2');
      if (style?.endsWith('C') || style?.endsWith('D')) result = result.filter(tab => tab !== 'T1');
    } else if (height === 52 || height === 65) {
      if (style?.endsWith('B') || style?.endsWith('D') || style?.endsWith('F') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T3');
      }
      if (style?.endsWith('C') || style?.endsWith('D') || style?.endsWith('G') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T2');
      }
      if (style?.endsWith('E') || style?.endsWith('F') || style?.endsWith('G') || style?.endsWith('H')) {
        result = result.filter(tab => tab !== 'T1');
      }
    }

    return result;
  });

  protected selections = computed(() =>
    this.zones().map(zone => ({
      zone,
      ...this.materialService.getSelection(zone)()
    }))
  );

  protected t0Dir = computed(() => {
    const style = this.selectedPanelStyle();
    if (!style) return false;

    if (style.endsWith('A')){
      return 'S0';
    }

    if (this.selectedHeight() === 32) {
      if(!style.endsWith('A')) {
        return 'C01';
      }
    }

    if (this.selectedHeight() === 39) {
      if(style.endsWith('B')){
        return 'S0';
      } else if(style.endsWith('C')) {
        return 'C01';
      } else if(style.endsWith('D')) {
        return 'C012/' + this.selectedHeight();
      }
    }

    if (this.selectedHeight() > 39) {
      if(style.endsWith('B') || style.endsWith('C') || style.endsWith('D')) {
        return 'S0';
      } else if(style.endsWith('E') || style.endsWith('F')) {
        return 'C01';
      } else if (style.endsWith('G')) {
        return 'C012/' + this.selectedHeight();
      } else if (style.endsWith('H')) {
        return 'C0123/' + this.selectedHeight();
      }
    }

    return false;
  });

  protected t1Dir = computed(() => {
    const style = this.selectedPanelStyle();
    if (!style) return false;

    if (style?.endsWith('A')) {
      return 'S1';
    }

    if (this.selectedHeight() === 39) {
      if (style.endsWith('B')) {
        return 'C12/' + this.selectedHeight();
      }
    }

    if (this.selectedHeight() > 39) {
      if (style.endsWith('B')) {
        return 'S1';
      } else if (style.endsWith('C')) {
        return 'C12/' + this.selectedHeight();
      } else if(style.endsWith('D')) {
        return 'C123/' + this.selectedHeight();
      }
    }

    return false;
  });

  protected t2Dir = computed(() => {
    const style = this.selectedPanelStyle();
    if (!style) return false;

    if (style?.endsWith('A') && this.selectedHeight() !== 32) {
      return 'S2/' + this.selectedHeight();
    }

    if (this.selectedHeight() === 39) {
      if(style.endsWith('C')) {
        return 'S2/' + this.selectedHeight();
      }
    }

    if (this.selectedHeight() > 39) {
      if (style.endsWith('B')) {
        return 'C23/' + this.selectedHeight();
      } else if(style.endsWith('E')) {
        return 'S2/' + this.selectedHeight();
      } else if(style.endsWith('F')) {
        return 'C23/' + this.selectedHeight();
      }
    }

    return false;
  });

  protected t3Dir = computed(() => {
    const style = this.selectedPanelStyle();
    if (!style) return false;

    if ((style?.endsWith('A') || style?.endsWith('C') || style?.endsWith('E') || style?.endsWith('G'))
      && this.selectedHeight() > 39) {
      return 'S3/' + this.selectedHeight();
    }

    return false;
  });
}
